import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import type { FormEvent } from 'react';

import {
  addInstruction,
  addJobInstruction,
  type JobInstruction,
  jobInstructionsQuery,
  removeJobInstruction,
  retireInstruction,
  updateInstruction,
  updateJobInstruction,
  type VenueInstruction,
  venueInstructionsQuery,
} from '../api/instructions.ts';
import { roomsQuery } from '../api/venues.ts';
import { Button } from '../components/button.tsx';
import { AddButton, EditButton, RemoveButton } from '../components/action-buttons.tsx';
import { icons } from '../components/icons.ts';
import { Popover } from '../components/popover.tsx';
import { TextArea } from '../components/text-area.tsx';

// CB-REQ VEN-09 at v1.974 (venue slice 1, part C): standing instructions. On the venue, the venue's
// own list, written by whoever holds venues.instructions; every upcoming job there follows it. On a
// job, its copies, which the office may edit or remove for that job alone. Crew read and acknowledge
// them on My shifts, so changing them asks crew to confirm again.

/**
 * The venue's rules, venue-wide first and then by room. Drawn at VEN-01 v1.9104 as the operator's
 * Rules board: a panel of rows, each with its scope; adding and editing open in a popover.
 */
export function VenueInstructions({
  venueId,
  mayWrite,
  onChanged,
}: {
  venueId: string;
  mayWrite: boolean;
  onChanged: () => Promise<void>;
}) {
  const list = useQuery(venueInstructionsQuery(venueId));
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: venueInstructionsQuery(venueId).queryKey });
    // Upcoming jobs' copies changed with it.
    await queryClient.invalidateQueries({ queryKey: ['jobs'] });
    await onChanged();
  };
  return (
    <section aria-labelledby="instructions" className="panel flex flex-col gap-2 p-4">
      <div className="flex flex-wrap items-center gap-2">
        <h2 id="instructions" className="panel-label flex-1">
          Instructions
        </h2>
        <span className="text-xs text-muted">On every job here · crew confirm</span>
        {mayWrite ? (
          <AddButton noun="Instruction" variant="quiet" onClick={() => setAdding(true)} />
        ) : null}
      </div>
      {adding ? (
        <Popover title="New instruction" onClose={() => setAdding(false)}>
          <InstructionForm venueId={venueId} onDone={() => setAdding(false)} onChanged={refresh} />
        </Popover>
      ) : null}
      {list.isError ? (
        <p role="alert" className="alert">
          {list.error.message}
        </p>
      ) : null}
      {list.data?.length === 0 ? <p className="text-sm text-ink-soft">None yet.</p> : null}
      <ul className="flex flex-col">
        {list.data?.map((i) => (
          <InstructionRow
            key={i.id}
            venueId={venueId}
            instruction={i}
            mayWrite={mayWrite}
            onChanged={refresh}
          />
        ))}
      </ul>
    </section>
  );
}

function InstructionRow({
  venueId,
  instruction,
  mayWrite,
  onChanged,
}: {
  venueId: string;
  instruction: VenueInstruction;
  mayWrite: boolean;
  onChanged: () => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [retiring, setRetiring] = useState(false);
  const retire = useMutation({
    mutationFn: () => retireInstruction(venueId, instruction.id),
    onSuccess: onChanged,
  });
  return (
    <li className="side-row flex-wrap text-sm">
      {editing ? (
        <Popover title="Edit instruction" onClose={() => setEditing(false)}>
          <InstructionForm
            venueId={venueId}
            instruction={instruction}
            onDone={() => setEditing(false)}
            onChanged={onChanged}
          />
        </Popover>
      ) : null}
      <icons.instructions size={16} aria-hidden className="shrink-0 text-muted" />
      <span className="min-w-0 flex-1 font-semibold whitespace-pre-line">{instruction.text}</span>
      <span className="tag">{instruction.room?.name ?? 'Venue'}</span>
      {mayWrite ? (
        retiring ? (
          <span className="flex w-full flex-wrap items-center gap-2">
            <span>Retire it? It comes off every upcoming job here.</span>
            <Button variant="quiet" disabled={retire.isPending} onClick={() => retire.mutate()}>
              Yes, retire
            </Button>
            <Button variant="quiet" onClick={() => setRetiring(false)}>
              Keep
            </Button>
          </span>
        ) : (
          <span className="flex gap-1">
            <EditButton what="instruction" onClick={() => setEditing(true)} />
            <Button variant="quiet" className="small-btn" onClick={() => setRetiring(true)}>
              Retire<span className="sr-only"> this instruction</span>
            </Button>
          </span>
        )
      ) : null}
      {retire.error ? (
        <p role="alert" className="w-full font-semibold text-accent-2-700">
          {retire.error.message}
        </p>
      ) : null}
    </li>
  );
}

function InstructionForm({
  venueId,
  instruction,
  onDone,
  onChanged,
}: {
  venueId: string;
  instruction?: VenueInstruction;
  onDone: () => void;
  onChanged: () => Promise<void>;
}) {
  const rooms = useQuery(roomsQuery(venueId));
  const [text, setText] = useState(instruction?.text ?? '');
  const [roomId, setRoomId] = useState(instruction?.room?.id ?? '');
  const [problem, setProblem] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: (input: { text: string; roomId: string | null }) =>
      instruction
        ? updateInstruction(venueId, instruction.id, input)
        : addInstruction(venueId, input),
    onSuccess: async () => {
      await onChanged();
      onDone();
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    if (!text.trim()) return setProblem('Write the instruction.');
    setProblem(undefined);
    save.mutate({ text: text.trim(), roomId: roomId || null });
  }
  return (
    <form onSubmit={submit} className="flex max-w-2xl flex-col gap-4">
      <TextArea
        label="Instruction"
        hint="Say why, if it helps it stick: “No haze — the chandeliers set off the detectors.”"
        value={text}
        error={problem}
        onChange={(e) => setText(e.currentTarget.value)}
      />
      <label className="flex flex-col gap-1.5">
        <span className="text-xs text-ink-soft">Applies to</span>
        <select className="input" value={roomId} onChange={(e) => setRoomId(e.currentTarget.value)}>
          <option value="">The whole venue</option>
          {rooms.data?.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} only
            </option>
          ))}
        </select>
      </label>
      {save.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {save.error.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          {instruction ? 'Save' : 'Add'}
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

/**
 * A job's copies: its own to edit or remove, flagged when the venue's rule has since changed; and
 * its own instructions, for the whole job or one of its rooms (VEN-09 at v1.975).
 */
export function JobInstructions({
  jobId,
  mayManage,
  rooms,
}: {
  jobId: string;
  mayManage: boolean;
  /** The rooms the job has lines in. */
  rooms: ReadonlyArray<{ id: string; name: string }>;
}) {
  const list = useQuery(jobInstructionsQuery(jobId));
  const [adding, setAdding] = useState(false);
  if (list.isPending) return null;
  if (list.isError) {
    return (
      <p role="alert" className="alert">
        {list.error.message}
      </p>
    );
  }
  const live = list.data.filter((i) => !i.removed);
  const removed = list.data.filter((i) => i.removed);
  if (list.data.length === 0 && !mayManage) return null;
  return (
    <section aria-labelledby="job-instructions" className="flex flex-col gap-3">
      <div className="flex items-center gap-3">
        <h2 id="job-instructions" className="section-title">
          Standing instructions
        </h2>
        {mayManage && !adding ? (
          <AddButton noun="Instruction" variant="quiet" onClick={() => setAdding(true)} />
        ) : null}
      </div>
      <p className="text-sm text-ink-soft">
        From the venue, and any added for this job. Changing one here changes it for this job only,
        and crew who confirmed are told.
      </p>
      {adding ? <JobOnlyForm jobId={jobId} rooms={rooms} onDone={() => setAdding(false)} /> : null}
      {list.data.length === 0 && !adding ? (
        <p className="text-sm text-ink-soft">None yet.</p>
      ) : null}
      <ul className="flex flex-col gap-3">
        {live.map((i) => (
          <JobInstructionRow key={i.id} jobId={jobId} copy={i} mayManage={mayManage} />
        ))}
      </ul>
      {removed.length > 0 ? (
        <details className="text-sm">
          <summary className="cursor-pointer text-ink-soft">
            Removed for this job ({removed.length})
          </summary>
          <ul className="mt-2 flex flex-col gap-2">
            {removed.map((i) => (
              <JobInstructionRow key={i.id} jobId={jobId} copy={i} mayManage={mayManage} />
            ))}
          </ul>
        </details>
      ) : null}
    </section>
  );
}

function JobInstructionRow({
  jobId,
  copy,
  mayManage,
}: {
  jobId: string;
  copy: JobInstruction;
  mayManage: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [text, setText] = useState(copy.text);
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: jobInstructionsQuery(jobId).queryKey });
  const change = useMutation({
    mutationFn: (changes: { text: string } | { reviewed: true }) =>
      updateJobInstruction(jobId, copy.id, changes),
    onSuccess: async () => {
      setEditing(false);
      await refresh();
    },
  });
  const remove = useMutation({
    mutationFn: () => removeJobInstruction(jobId, copy.id),
    onSuccess: refresh,
  });
  const error = change.error ?? remove.error;
  return (
    <li
      className={`flex flex-col gap-1 rounded-md p-3 text-sm ${copy.removed ? 'text-ink-soft' : 'bg-tile'}`}
    >
      <span className="flex flex-wrap gap-1">
        <span className="tag">
          {copy.room?.name ?? (copy.jobOnly ? 'Whole job' : 'Whole venue')}
        </span>
        {copy.jobOnly ? <span className="tag">This job only</span> : null}
        {copy.edited && !copy.jobOnly ? <span className="tag">Changed for this job</span> : null}
      </span>
      {editing ? (
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (text.trim()) change.mutate({ text: text.trim() });
          }}
        >
          <TextArea
            label="Instruction for this job"
            value={text}
            onChange={(e) => setText(e.currentTarget.value)}
          />
          <span className="flex gap-2">
            <Button type="submit" disabled={change.isPending || !text.trim()}>
              Save
            </Button>
            <Button type="button" variant="quiet" onClick={() => setEditing(false)}>
              Cancel
            </Button>
          </span>
        </form>
      ) : (
        <span className="whitespace-pre-line">{copy.text}</span>
      )}
      {copy.venueChanged ? (
        <span className="notice flex flex-wrap items-center gap-2">
          {copy.jobOnly
            ? 'This job has moved venue since this was written. Check it still applies.'
            : `The venue has changed or retired this rule since it was ${
                copy.removed ? 'removed' : 'changed'
              } here. Check the venue’s page.`}
          {mayManage ? (
            <Button
              variant="quiet"
              disabled={change.isPending}
              onClick={() => change.mutate({ reviewed: true })}
            >
              Checked
            </Button>
          ) : null}
        </span>
      ) : null}
      {error ? (
        <p role="alert" className="font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
      {mayManage && !copy.removed && !editing ? (
        <span className="flex flex-wrap gap-2">
          {removing ? (
            <>
              <span>Remove it from this job?</span>
              <Button variant="quiet" disabled={remove.isPending} onClick={() => remove.mutate()}>
                Yes, remove
              </Button>
              <Button variant="quiet" onClick={() => setRemoving(false)}>
                Keep
              </Button>
            </>
          ) : (
            <>
              <EditButton what="instruction for this job" onClick={() => setEditing(true)} />
              <RemoveButton what="instruction from this job" onClick={() => setRemoving(true)} />
            </>
          )}
        </span>
      ) : null}
    </li>
  );
}

/** VEN-09 at v1.975: an instruction for this job alone. */
function JobOnlyForm({
  jobId,
  rooms,
  onDone,
}: {
  jobId: string;
  rooms: ReadonlyArray<{ id: string; name: string }>;
  onDone: () => void;
}) {
  const queryClient = useQueryClient();
  const [text, setText] = useState('');
  const [roomId, setRoomId] = useState('');
  const [problem, setProblem] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: (input: { text: string; roomId: string | null }) => addJobInstruction(jobId, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: jobInstructionsQuery(jobId).queryKey });
      onDone();
    },
  });
  function submit(event: FormEvent) {
    event.preventDefault();
    if (!text.trim()) return setProblem('Write the instruction.');
    setProblem(undefined);
    save.mutate({ text: text.trim(), roomId: roomId || null });
  }
  return (
    <form onSubmit={submit} className="flex max-w-2xl flex-col gap-4 border-2 border-divider p-4">
      <TextArea
        label="Instruction for this job"
        hint="For this show only, such as the client's own dock procedure. The venue's list stays as it is."
        value={text}
        error={problem}
        onChange={(e) => setText(e.currentTarget.value)}
      />
      {rooms.length > 0 ? (
        <label className="flex flex-col gap-1.5">
          <span className="text-xs text-ink-soft">Applies to</span>
          <select
            className="input"
            value={roomId}
            onChange={(e) => setRoomId(e.currentTarget.value)}
          >
            <option value="">The whole job</option>
            {rooms.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} only
              </option>
            ))}
          </select>
        </label>
      ) : null}
      {save.error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {save.error.message}
        </p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" disabled={save.isPending}>
          Add
        </Button>
        <Button type="button" variant="quiet" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
